// nativeHttpAdapter.js — EL TRANSPORTE NATIVO para las llamadas JSON de la app
// (F6.7b, pasada final 3).
//
// El bug que cierra (medido con el microscopio, log s78i8t 06:44:35→06:47:08):
// tras NET.UP, TODAS las operaciones (documents/login/probe/bundle) fallaron
// con ECONNABORTED durante ~150 s y luego TODAS revivieron juntas. Eso no es
// DNS ni backend: es una conexión pooled ZOMBI de WKWebView tras el cambio de
// interfaz — las peticiones nuevas del WebView se forman detrás del cadáver
// hasta que WebKit lo desecha. Nuestra tubería está sana (el ciclo limpio
// previo tardó 0.4 s); el enfermo es el transporte del WebView.
//
// La salida: un ADAPTER de axios que enruta la petición por CapacitorHttp —
// el plugin HTTP que ya viene en @capacitor/core (CERO plugins nuevos, CERO
// cambios en ios/, CERO parche global de fetch en capacitor.config). La
// petición viaja por URLSession, cuyo pool es del sistema y no comparte los
// cadáveres del WebView: tras NET.UP abre conexión fresca en segundos.
//
// REGLAS DURAS:
//   1. SOLO app-build lo usa: api.js lo pasa como `adapter` únicamente cuando
//      IS_APP_BUILD. En web este módulo queda inerte (ni siquiera carga
//      @capacitor/core — el import es dinámico, mismo patrón que tokenStore).
//   2. SOLO peticiones JSON: cualquier FormData (subida de documento, imagen
//      de flashcard) sigue por el camino actual de axios (XHR del WebView),
//      timeout:0 incluido. Una subida de 50 MB por URLSession sin streaming ni
//      onUploadProgress sería una regresión, no un arreglo. También delegan
//      los responseType binarios (blob/arraybuffer) y cualquier petición con
//      callbacks de progreso.
//   3. PARIDAD DE CONTRATOS con axios, porque los interceptores de api.js y
//      los clasificadores (isNetworkShapedError, isAuthRejection,
//      classifyAuthFailure) leen la FORMA del error:
//        - HTTP 4xx/5xx → AxiosError CON `response` (status/data/headers):
//          el interceptor de 401→refresh y los mensajes del backend siguen
//          funcionando igual.
//        - fallo nativo (sin respuesta) → AxiosError ERR_NETWORK sin
//          `response`: forma-de-red. `msg` conserva el texto del error nativo
//          para que NET.ERR diga QUÉ pasó, no solo "falló".
//        - techo de espera → AxiosError ECONNABORTED, mismo código y mismo
//          mensaje que el timeout clásico de axios (los registros del visor
//          quedan comparables antes/después del cambio de transporte).
//        - signal abortada (abortAllInFlight al perder la señal) →
//          CanceledError ERR_CANCELED, igual que un abort de axios.
//      En los cuatro casos err.config viaja, así que el interceptor que da de
//      baja del registro de en-vuelo (releaseController) sigue operando.
//
// LO QUE NO HACE: no reintenta, no clasifica, no toca sesión. Es un cable:
// la política (pleito de "Conectando…", refresh, atajos offline) vive donde
// siempre ha vivido.

import axios, { AxiosError, CanceledError } from 'axios';

// ¿Esta petición puede viajar por el canal nativo? Estructural, no una lista
// de rutas (misma filosofía que isUploadRequest en api.js): JSON/texto sin
// cuerpo FormData y sin callbacks de progreso. Todo lo demás delega.
export function isNativeTransportEligible(config) {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return false;
  if (config.responseType && config.responseType !== 'json' && config.responseType !== 'text') return false;
  if (config.onUploadProgress || config.onDownloadProgress) return false;
  return true;
}

// URL completa: baseURL + url relativa (+ params si algún día alguien los usa;
// hoy el censo dice que nadie). Una URL ya absoluta no se toca.
function buildRequestUrl(config) {
  const base = config.baseURL || '';
  const path = config.url || '';
  const isAbsolute = /^([a-z][a-z\d+\-.]*:)?\/\//i.test(path);
  let full = (isAbsolute || !base)
    ? path
    : `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  if (config.params) {
    const qs = new URLSearchParams(config.params).toString();
    if (qs) full += (full.includes('?') ? '&' : '?') + qs;
  }
  return full;
}

// Los headers como objeto plano de strings (CapacitorHttp no entiende
// AxiosHeaders) y sin valores nulos.
function plainHeaders(config) {
  const h = config.headers;
  const json = h && typeof h.toJSON === 'function' ? h.toJSON() : { ...(h || {}) };
  const out = {};
  for (const [k, v] of Object.entries(json)) {
    if (v === undefined || v === null || v === false) continue;
    out[k] = String(v);
  }
  return out;
}

// El cuerpo para el canal nativo. El adapter corre DESPUÉS de transformRequest,
// así que un POST JSON llega aquí como STRING ya serializado. El puente nativo
// serializa él mismo el objeto de opciones, y su manejo canónico de
// application/json espera el OBJETO (un string se re-serializaría entre
// comillas: cuerpo doblemente codificado). Se devuelve al objeto original.
function nativeBody(config, headers) {
  let data = config.data;
  if (typeof data === 'string') {
    const ct = Object.entries(headers).find(([k]) => k.toLowerCase() === 'content-type');
    if (ct && /json/i.test(String(ct[1]))) {
      try { data = JSON.parse(data); } catch (e) { /* no era JSON: viaja tal cual */ }
    }
  }
  return data;
}

/**
 * Adapter de axios que enruta por CapacitorHttp (URLSession/OkHttp). Las
 * peticiones no elegibles (FormData, binarios, progreso) delegan al adapter
 * por defecto de axios — el mismo camino de hoy, sin cambios.
 */
export async function nativeHttpAdapter(config) {
  if (!isNativeTransportEligible(config)) {
    const fallback = axios.getAdapter(axios.defaults.adapter);
    return fallback(config);
  }

  // Import dinámico (patrón tokenStore): webpack lo parte en un chunk que el
  // bundle web jamás pide, porque en web este adapter no se instala.
  const { CapacitorHttp } = await import('@capacitor/core');

  const signal = config.signal;
  if (signal && signal.aborted) throw new CanceledError(null, config);

  const headers = plainHeaders(config);
  const url = buildRequestUrl(config);
  const data = nativeBody(config, headers);
  const timeout = typeof config.timeout === 'number' && config.timeout > 0 ? config.timeout : 0;

  const options = {
    url,
    method: (config.method || 'get').toUpperCase(),
    headers,
    ...(data !== undefined ? { data } : {}),
    // El techo también viaja al lado nativo; el timer JS de abajo es el
    // cinturón que garantiza la forma exacta del error de axios.
    ...(timeout ? { connectTimeout: timeout, readTimeout: timeout } : {}),
  };

  return new Promise((resolve, reject) => {
    let settled = false;
    let timer = null;
    let onAbort = null;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
    };
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    if (signal) {
      onAbort = () => settle(reject, new CanceledError(null, config));
      signal.addEventListener('abort', onAbort);
    }
    if (timeout) {
      timer = setTimeout(() => settle(reject, new AxiosError(
        `timeout of ${timeout}ms exceeded`, AxiosError.ECONNABORTED, config
      )), timeout);
    }

    CapacitorHttp.request(options).then((res) => {
      const response = {
        data: res.data,
        status: res.status,
        statusText: '',
        headers: res.headers || {},
        config,
        request: null,
      };
      const validate = config.validateStatus;
      if (!validate || validate(response.status)) {
        settle(resolve, response);
      } else {
        // Mismo settle que axios: 4xx → ERR_BAD_REQUEST, 5xx → ERR_BAD_RESPONSE,
        // y SIEMPRE con `response` — así isAuthRejection ve su 401 y el
        // interceptor de refresh se dispara igual que con XHR.
        settle(reject, new AxiosError(
          'Request failed with status code ' + response.status,
          [AxiosError.ERR_BAD_REQUEST, AxiosError.ERR_BAD_RESPONSE][Math.floor(response.status / 100) - 4],
          config,
          null,
          response
        ));
      }
    }, (err) => {
      // Fallo del canal nativo: sin `response` → forma-de-red. El mensaje
      // nativo se conserva para que NET.ERR {code, msg} siga diciendo QUÉ
      // falló (ERR_NETWORK + "The Internet connection appears to be offline",
      // no un "unknown" mudo).
      settle(reject, new AxiosError(
        String((err && err.message) || err || 'Network Error'),
        AxiosError.ERR_NETWORK,
        config,
        null
      ));
    });
  });
}

export default nativeHttpAdapter;
